/**
 * Pure automation-curve math. Lanes are piecewise-linear curves of
 * { beat, value } points applied to AudioParams by the transport scheduler.
 * Kept free of React/Web Audio so curve behavior is unit-testable.
 */

/** Parameters that can be automated per track (v1: mixer params). */
export const AUTOMATION_PARAMS = {
  volume: { id: 'volume', label: 'Volume', min: 0, max: 1.25, default: 1 },
  pan: { id: 'pan', label: 'Pan', min: -1, max: 1, default: 0 }
};

export function clampParamValue(paramId, value) {
  const def = AUTOMATION_PARAMS[paramId];
  // Number(null) === 0, which would silently zero missing values.
  const absent = value === null || value === undefined || value === '';
  const num = absent ? NaN : Number(value);
  if (!def) return Number.isFinite(num) ? num : 0;
  if (!Number.isFinite(num)) return def.default;
  return Math.max(def.min, Math.min(def.max, num));
}

/**
 * Normalize raw point data: numeric beats/values, clamped to the param's
 * domain, sorted by beat, deduped (last write wins per beat).
 */
export function normalizeAutomationPoints(paramId, points) {
  if (!Array.isArray(points)) return [];
  const byBeat = new Map();
  points.forEach((point) => {
    const beat = Number(point?.beat);
    if (!Number.isFinite(beat) || beat < 0) return;
    const rounded = Math.round(beat * 1000) / 1000;
    byBeat.set(rounded, {
      beat: rounded,
      value: clampParamValue(paramId, point?.value)
    });
  });
  return [...byBeat.values()].sort((a, b) => a.beat - b.beat);
}

/**
 * Curve value at a beat: linear interpolation between surrounding points,
 * flat extension before the first and after the last point.
 * Points must be sorted by beat (normalizeAutomationPoints guarantees this).
 */
export function valueAtBeat(points, beat, defaultValue) {
  if (!Array.isArray(points) || points.length === 0) return defaultValue;
  if (beat <= points[0].beat) return points[0].value;
  const last = points[points.length - 1];
  if (beat >= last.beat) return last.value;

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (beat <= next.beat) {
      const span = next.beat - prev.beat;
      if (span <= 1e-9) return next.value;
      const t = (beat - prev.beat) / span;
      return prev.value + ((next.value - prev.value) * t);
    }
  }
  return last.value;
}

/** Points with fromBeat < beat <= toBeat (slice-interior ramp targets). */
export function pointsInRange(points, fromBeat, toBeat) {
  if (!Array.isArray(points)) return [];
  return points.filter((p) => p.beat > fromBeat && p.beat <= toBeat);
}

/** The automation lanes a render applies: volume and pan lanes that have points. */
export function automationLanesForRender(automation) {
  const lanes = [];
  Object.entries(automation || {}).forEach(([trackId, trackLanes]) => {
    Object.entries(trackLanes || {}).forEach(([paramId, lane]) => {
      const def = AUTOMATION_PARAMS[paramId];
      if (!def || !Array.isArray(lane?.points) || lane.points.length === 0) return;
      lanes.push({ trackId, paramId, def, points: lane.points });
    });
  });
  return lanes;
}

/**
 * Write one lane onto an AudioParam for a whole offline render from beat 0,
 * the same ramps live playback schedules slice by slice.
 */
export function renderAutomation(param, points, def, { secondsPerBeat, endBeat }) {
  if (!param || !Array.isArray(points) || points.length === 0) return;
  param.setValueAtTime(valueAtBeat(points, 0, def.default), 0);
  pointsInRange(points, 0, endBeat).forEach((point) => {
    param.linearRampToValueAtTime(point.value, point.beat * secondsPerBeat);
  });
  param.linearRampToValueAtTime(valueAtBeat(points, endBeat, def.default), endBeat * secondsPerBeat);
}
